import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { WifiOff, RefreshCw } from "lucide-react";

interface NetworkErrorProps {
  onRetry?: () => void;
}

export function NetworkError({ onRetry }: NetworkErrorProps) {
  const { t } = useTranslation();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default retry behavior - reload the page
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <WifiOff className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('networkError.title', 'No Internet Connection')}
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            {t('networkError.description', 'Please check your internet connection and try again.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="text-sm text-gray-500 dark:text-gray-400 space-y-2">
            <p>{t('networkError.suggestions.title', 'Try the following:')}</p>
            <ul className="text-left space-y-1 max-w-xs mx-auto">
              <li>• {t('networkError.suggestions.checkConnection', 'Check your internet connection')}</li>
              <li>• {t('networkError.suggestions.checkWifi', 'Make sure Wi-Fi is enabled')}</li>
              <li>• {t('networkError.suggestions.tryAgain', 'Try again in a few moments')}</li>
            </ul>
          </div>
          
          <Button onClick={handleRetry} className="w-full">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('networkError.retry', 'Try Again')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
} 